/**
 * Sole collect presentation predicates — single source of truth for
 * CollectPickupSheetRetail, ShopCheckoutOverlays, and phone-first JourneyShell.
 *
 * Journey picker may still appear when multiple journeys exist; these helpers
 * describe handoff/timing/title framing only so announce chrome does not contradict.
 */

export type CollectSoleTimingVisibility = {
  readonly showNowTiming: boolean;
  readonly showLaterTiming: boolean;
};

export type CollectSolePresentationInput = CollectSoleTimingVisibility & {
  /** Count from resolveRetailHandoffChoices (self/bar collapse), not raw modes. */
  readonly handoffChoiceCount: number;
  /**
   * When handoff choices are empty, treat a single pickup point as sole "place"
   * announcement (G4). Ignored when handoffChoiceCount > 0.
   */
  readonly pickupPointCount?: number;
};

export type CollectSoleCopyKind =
  | 'both_multi'
  | 'both_sole'
  | 'method_sole_time_multi'
  | 'method_multi_time_sole';

/** True when exactly one retail handoff card would be shown. */
export function isSoleHandoffAnnouncement(handoffChoiceCount: number): boolean {
  return handoffChoiceCount === 1;
}

/**
 * True when zero handoff choices and exactly one pickup point — announce place,
 * do not render a fake chooser list (G4).
 */
export function isSolePointWithoutHandoffAnnouncement(
  handoffChoiceCount: number,
  pickupPointCount: number,
): boolean {
  return handoffChoiceCount === 0 && pickupPointCount === 1;
}

/**
 * Sole timing = exactly one of ASAP or Later is available (not both, not neither).
 * Covers sole NOW and sole LATER (G1).
 */
export function isSoleTimingAnnouncement(
  visibility: CollectSoleTimingVisibility,
): boolean {
  const { showNowTiming, showLaterTiming } = visibility;
  return (
    (showNowTiming && !showLaterTiming) || (!showNowTiming && showLaterTiming)
  );
}

/** Both method and timing are sole (full titleSole / introSole). */
export function isSoleCollectOptions(input: CollectSolePresentationInput): boolean {
  const handoffSole =
    isSoleHandoffAnnouncement(input.handoffChoiceCount) ||
    isSolePointWithoutHandoffAnnouncement(
      input.handoffChoiceCount,
      input.pickupPointCount ?? 0,
    );
  return handoffSole && isSoleTimingAnnouncement(input);
}

/**
 * Classify copy framing for sheet header / overlay / shell.
 * - both_sole → titleSole / introSole
 * - method_sole_time_multi → do not invite choosing a method (G5)
 * - method_multi_time_sole → keep method chooser framing (title/intro)
 * - both_multi → title / intro
 */
export function resolveCollectSoleCopyKind(
  input: CollectSolePresentationInput,
): CollectSoleCopyKind {
  const handoffSole =
    isSoleHandoffAnnouncement(input.handoffChoiceCount) ||
    isSolePointWithoutHandoffAnnouncement(
      input.handoffChoiceCount,
      input.pickupPointCount ?? 0,
    );
  const timingSole = isSoleTimingAnnouncement(input);
  if (handoffSole && timingSole) {
    return 'both_sole';
  }
  if (handoffSole && !timingSole) {
    return 'method_sole_time_multi';
  }
  if (!handoffSole && timingSole) {
    return 'method_multi_time_sole';
  }
  return 'both_multi';
}

/** i18n key for collect sheet / overlay / JourneyShell title. */
export function resolvePickupCollectTitleKey(
  input: CollectSolePresentationInput,
): 'pickup.titleSole' | 'pickup.titleMethodSole' | 'pickup.title' {
  const kind = resolveCollectSoleCopyKind(input);
  if (kind === 'both_sole') {
    return 'pickup.titleSole';
  }
  if (kind === 'method_sole_time_multi') {
    return 'pickup.titleMethodSole';
  }
  return 'pickup.title';
}

/** i18n key for collect intro under the title (sheet header or overlay banner). */
export function resolvePickupCollectIntroKey(
  input: CollectSolePresentationInput,
): 'pickup.introSole' | 'pickup.introMethodSole' | 'pickup.intro' {
  const kind = resolveCollectSoleCopyKind(input);
  if (kind === 'both_sole') {
    return 'pickup.introSole';
  }
  if (kind === 'method_sole_time_multi') {
    return 'pickup.introMethodSole';
  }
  return 'pickup.intro';
}

/**
 * Whether overlay/sheet should show the sole intro status line under chrome.
 * Shown for both_sole and method_sole_time_multi (announce method framing).
 */
export function shouldShowSoleCollectIntroBanner(
  input: CollectSolePresentationInput,
): boolean {
  const kind = resolveCollectSoleCopyKind(input);
  return kind === 'both_sole' || kind === 'method_sole_time_multi';
}
